import { useState, useEffect } from 'react';
import { useCountry } from '../../contexts/CountryContext';
import { api } from '../../lib/api';
import { formatDateTime, getSeverityColor, getStatusColor } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { Plus, AlertTriangle, CheckCircle2 } from 'lucide-react';

const exceptionTypes = [
  'Missed Collection',
  'Document Expired',
  'Compliance Breach',
  'Vehicle Issue',
  'Communication Failure',
  'Quality Issue',
  'Other',
];

const severities = ['low', 'medium', 'high', 'critical'];

export function ExceptionsPage() {
  const { countries, selectedCountry } = useCountry();
  const [exceptions, setExceptions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedExceptionId, setSelectedExceptionId] = useState(null);
  const [resolveData, setResolveData] = useState({ root_cause: '', corrective_action: '' });
  const [formData, setFormData] = useState({
    vendor_id: '',
    country_code: '',
    exception_type: '',
    severity: 'medium',
    title: '',
    description: '',
  });

  useEffect(() => {
    fetchData();
  }, [selectedCountry]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedCountry ? { country_code: selectedCountry } : {};
      const [exceptionsData, vendorsData] = await Promise.all([
        api.getExceptions(params),
        api.getVendors(),
      ]);
      setExceptions(exceptionsData);
      setVendors(vendorsData);
    } catch (error) {
      toast.error('Failed to load exceptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateException = async (e) => {
    e.preventDefault();
    try {
      await api.createException(formData);
      toast.success('Exception created');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create exception');
    }
  };

  const handleResolveException = async () => {
    try {
      await api.resolveException(selectedExceptionId, resolveData.root_cause, resolveData.corrective_action);
      toast.success('Exception resolved');
      setResolveDialogOpen(false);
      setResolveData({ root_cause: '', corrective_action: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to resolve exception');
    }
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || '-';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="exceptions-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Exceptions</h1>
          <p className="text-muted-foreground mt-1">
            Track and resolve non-conformance issues
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-exception-btn">
              <Plus className="w-4 h-4 mr-2" />
              Report Exception
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>Report Exception</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateException} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vendor *</Label>
                  <Select
                    value={formData.vendor_id}
                    onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map((v) => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Country *</Label>
                  <Select
                    value={formData.country_code}
                    onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type *</Label>
                  <Select
                    value={formData.exception_type}
                    onValueChange={(value) => setFormData({ ...formData, exception_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {exceptionTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Severity *</Label>
                  <Select
                    value={formData.severity}
                    onValueChange={(value) => setFormData({ ...formData, severity: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {severities.map((sev) => (
                        <SelectItem key={sev} value={sev} className="capitalize">{sev}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Exception</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Resolve Dialog */}
      <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Exception</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Root Cause *</Label>
              <Textarea
                value={resolveData.root_cause}
                onChange={(e) => setResolveData({ ...resolveData, root_cause: e.target.value })}
                placeholder="Describe the root cause..."
              />
            </div>
            <div>
              <Label>Corrective Action *</Label>
              <Textarea
                value={resolveData.corrective_action}
                onChange={(e) => setResolveData({ ...resolveData, corrective_action: e.target.value })}
                placeholder="Describe the corrective action taken..."
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleResolveException}>
                Resolve Exception
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Severity</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
                </TableRow>
              ) : exceptions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <AlertTriangle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No exceptions found</p>
                  </TableCell>
                </TableRow>
              ) : (
                exceptions.map((exc) => (
                  <TableRow key={exc.id}>
                    <TableCell>
                      <Badge className={getSeverityColor(exc.severity)}>
                        {exc.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{getVendorName(exc.vendor_id)}</TableCell>
                    <TableCell>{exc.exception_type}</TableCell>
                    <TableCell className="max-w-xs truncate">{exc.title}</TableCell>
                    <TableCell>{formatDateTime(exc.created_at)}</TableCell>
                    <TableCell>
                      {exc.sla_resolution_deadline && (
                        <span className="text-sm text-muted-foreground">
                          {formatDateTime(exc.sla_resolution_deadline)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(exc.status)}>
                        {exc.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {exc.status === 'open' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedExceptionId(exc.id);
                            setResolveDialogOpen(true);
                          }}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" />
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
